import ColorSwatches, {
  Container as ColorSwatchesContainer,
} from "@components/ColorSwatches"
import ProductBlock from "@components/ProductBlock"
import { ProductTagProps } from "@components/ProductTag"
import breakpoints from "@constants/breakpoints"
import { getMarketAmount } from "@utils/pricing"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

export const Container = styled.div`
  @media ${breakpoints.laptop} {
    ${ColorSwatchesContainer} {
      margin-top: 0;
    }
  }
`

const Swatches = styled.div<{ $hideMobileSwatches?: boolean }>`
  @media ${breakpoints.mobileOnly} {
    ${({ $hideMobileSwatches }) =>
      $hideMobileSwatches ? "display: none;" : ""}
  }

  @media ${breakpoints.laptop} {
    flex-direction: column;
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
  }
`

const Spacer = styled.div`
  width: 100%;
  height: 18px;
`

interface ProductBlockWithSwatchesProps {
  clickOnly?: boolean
  colorFilters?: any
  customerCountryCode?: string
  customPrint?: boolean
  hideMobileSwatches?: boolean
  images?: Array<string>
  onClick?: any
  productName: string
  selectedDevice?: string
  selectedDeviceSlug?: string
  slug: string
  tags?: Array<ProductTagProps>
  variants: Array<{
    color: string
    compareAtPrice?: string
    deviceName?: string
    deviceSlug?: string
    hexString?: string
    images: Array<string>
    isUnavailable?: boolean
    name: string
    price: string
    sku: string
    swatchImage?: string
  }>
}

const ProductBlockWithSwatches: React.FC<ProductBlockWithSwatchesProps> = (
  props: ProductBlockWithSwatchesProps
) => {
  const validVariants = React.useMemo(() => {
    return R.reject((variant: any) => R.isNil(variant.images))(props.variants)
  }, [props.variants.length])

  const variants = React.useMemo(() => {
    if (props.customPrint) {
      return [validVariants[0]]
    } else {
      return validVariants
    }
  }, [props.customPrint, props.selectedDeviceSlug, props.variants.length])

  const selectedSku = React.useMemo(() => {
    if (props.selectedDevice) {
      const selectedVariant = validVariants.find(
        (variant) => variant.deviceName === props.selectedDevice
      )

      if (selectedVariant) {
        return selectedVariant.sku
      }

      return null
    }
  }, [props.selectedDevice])

  const priceRange = React.useMemo(() => {
    const prices = variants
      .map((variant) => {
        const marketPrice = getMarketAmount(
          variant,
          props.customerCountryCode || "US",
          "price"
        )

        if (marketPrice) {
          return typeof marketPrice === "number"
            ? marketPrice
            : parseFloat(marketPrice.replace("$", ""))
        }

        return null
      })
      .filter((price) => price !== null) as number[]

    if (prices.length === 0) {
      return {}
    }

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const formattedRange =
      minPrice === maxPrice ? `$${minPrice}` : `$${minPrice} - $${maxPrice}`

    return {
      formattedRange,
      maxPrice,
      minPrice,
    }
  }, [variants.length, props.customerCountryCode])

  const maxCompareAtPrice = React.useMemo(() => {
    const prices = variants
      .map((variant) => {
        const marketCompareAtPrice = getMarketAmount(
          variant,
          props.customerCountryCode || "US",
          "compareAtPrice"
        )
        if (marketCompareAtPrice) {
          return typeof marketCompareAtPrice === "number"
            ? marketCompareAtPrice
            : parseFloat(marketCompareAtPrice.replace("$", ""))
        }

        return null
      })
      .filter((price) => price !== null) as number[]

    if (!prices || prices.length === 0) {
      return null
    } else {
      return Math.max(...prices)
    }
  }, [variants.length, props.customerCountryCode])

  const showMaxCompareAtPrice = React.useMemo(() => {
    return (
      priceRange?.maxPrice &&
      maxCompareAtPrice &&
      maxCompareAtPrice > priceRange.maxPrice
    )
  }, [priceRange, maxCompareAtPrice])

  const [activeVariant, setActiveVariant] = React.useState(variants[0])
  const [swatchClicked, setSwatchClicked] = React.useState(false)

  React.useEffect(() => {
    if (props.colorFilters?.length) {
      const filteredVariant = variants.find(
        ({ color, hexString, swatchImage }) => {
          const matchingFilter = props.colorFilters.find(
            ({
              hexString: filterHexString,
              label,
              swatchImage: filterSwatchImage,
            }) => {
              return (
                color === label &&
                (hexString === filterHexString ||
                  swatchImage === filterSwatchImage)
              )
            }
          )
          return !!matchingFilter
        }
      )

      if (filteredVariant) {
        setActiveVariant(filteredVariant)
        return
      }
    }

    setActiveVariant(variants[0])
    setSwatchClicked(false)
  }, [variants.length, props.colorFilters?.length])

  const getSwatchClickHandler = (variant) => () => {
    setActiveVariant(variant)
    setSwatchClicked(true)
  }

  const swatchColors = React.useMemo(
    () =>
      variants.length > 1
        ? (R.uniqBy(
            ({ hexString, swatchImage }: any) => swatchImage || hexString,
            variants.map((variant) => ({
              ...R.pick(["name", "hexString", "swatchImage", "isUnavailable"])(
                variant
              ),
              onClick: getSwatchClickHandler(variant),
            }))
          ) as any)
        : [],
    [variants.length]
  )

  return (
    <Container>
      <ProductBlock
        clickOnly={props.clickOnly}
        compactVersion={props.hideMobileSwatches}
        customPrint={props.customPrint}
        decoratedSubtitle={
          showMaxCompareAtPrice ? `$${maxCompareAtPrice}` : undefined
        }
        galleryImage={activeVariant?.images?.[1]}
        image={activeVariant?.images && activeVariant.images[0]}
        lifestyleImage={props.images?.[0]}
        onClick={props.onClick}
        paddingTop={!props.tags || props.tags.length === 0}
        selectedDevice={props.selectedDevice}
        selectedDeviceSlug={props.selectedDeviceSlug}
        sku={selectedSku || activeVariant?.sku}
        slug={props.slug}
        subtitle={priceRange?.formattedRange ? priceRange?.formattedRange : "-"}
        swatchClicked={swatchClicked}
        tags={props.tags}
        title={props.productName}
      />
      <Swatches $hideMobileSwatches={props.hideMobileSwatches}>
        <Spacer />
        <ColorSwatches colors={swatchColors} />
      </Swatches>
    </Container>
  )
}

export default ProductBlockWithSwatches
